import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScanSearch, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  useMatchStolenMutation,
  type MatchStolenResult,
} from '@/client/api/assets/assets.mutation';
import { getApiErrorMessage } from '@/client/utils/errors';
import { useMatchStolenUploadMutation } from '@/client/api/assets/duplicates';

interface MatchStolenDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-fills the URL (e.g. "Find similar" from an existing asset). */
  initialUrl?: string;
}

// Closeness bucket for a Hamming distance — 0 is an exact perceptual match.
const distanceTone = (d: number) => {
  if (d === 0) return 'bg-red-100 text-red-700';
  if (d <= 4) return 'bg-amber-100 text-amber-700';
  return 'bg-gray-100 text-gray-600';
};

const MatchStolenDialog: React.FC<MatchStolenDialogProps> = ({ open, onClose, initialUrl }) => {
  const [url, setUrl] = useState('');
  const [maxHamming, setMaxHamming] = useState(8);
  const [result, setResult] = useState<MatchStolenResult | null>(null);
  const match = useMatchStolenMutation();
  const upload = useMatchStolenUploadMutation();
  const [file, setFile] = useState<File | null>(null);
  const pending = match.isPending || upload.isPending;

  useEffect(() => {
    if (open) {
      setUrl(initialUrl ?? '');
      setFile(null);
      setResult(null);
    }
  }, [open, initialUrl]);

  const run = async () => {
    const trimmed = url.trim();
    if (!trimmed && !file) {
      toast.error('Paste an image URL or choose a file to search.');
      return;
    }
    try {
      // An uploaded file is hashed on the server and not stored.
      const res = file
        ? await upload.mutateAsync({ file, max_hamming: maxHamming })
        : await match.mutateAsync({ url: trimmed, max_hamming: maxHamming, limit: 50 });
      setResult(res);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Reverse-image lookup failed'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-hidden sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanSearch className="h-5 w-5 text-blue-600" /> Reverse-image lookup
          </DialogTitle>
          <DialogDescription>
            Find assets whose perceptual hash is within the given Hamming distance — used to
            spot stolen or duplicated photos.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-end gap-3">
          <div className="flex-1 space-y-1">
            <Label htmlFor="match-url">Image URL</Label>
            <Input
              id="match-url"
              placeholder="https://…/photo.jpg"
              value={url}
              onChange={(e) => { setUrl(e.target.value); setFile(null); }}
              onKeyDown={(e) => e.key === 'Enter' && run()}
            />
          </div>
          <div className="w-28 space-y-1">
            <Label htmlFor="match-hamming">Max distance</Label>
            <Input
              id="match-hamming"
              type="number"
              min={0}
              max={32}
              value={maxHamming}
              onChange={(e) => setMaxHamming(Number(e.target.value))}
            />
          </div>
          <Button onClick={run} disabled={pending}>
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
          </Button>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>or upload a photo:</span>
          <input
            type="file"
            accept="image/*"
            aria-label="Photo to search for"
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); if (e.target.files?.[0]) setUrl(''); }}
          />
          {file && <button className="text-blue-600 hover:underline" onClick={() => setFile(null)}>clear</button>}
        </div>

        <div className="mt-2 max-h-[45vh] overflow-y-auto">
          {result && (
            <div className="mb-2 text-xs text-muted-foreground">
              {result.match_count} match{result.match_count === 1 ? '' : 'es'} within{' '}
              {result.max_hamming} bits · query hash{' '}
              <span className="font-mono">{result.query_phash}</span>
            </div>
          )}

          {result && result.matches.length === 0 && (
            <div className="rounded-lg border border-dashed py-10 text-center text-sm text-gray-500">
              No matching assets found.
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {result?.matches.map((m) => (
              <div key={m.asid} className="overflow-hidden rounded-lg border bg-card">
                <div className="relative aspect-square bg-muted">
                  {m.public_url ? (
                    <img
                      src={m.public_url}
                      alt={`Asset ${m.asid}`}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                      No preview
                    </div>
                  )}
                  <span
                    className={`absolute left-1.5 top-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold ${distanceTone(m.hamming_distance)}`}
                  >
                    {m.hamming_distance === 0 ? 'Exact' : `Δ${m.hamming_distance}`}
                  </span>
                </div>
                <div className="p-2 text-[11px] text-muted-foreground">
                  <a className="font-medium text-blue-600 hover:underline" href={`/admin/assets?q=${m.asid}`} target="_blank" rel="noreferrer">Asset #{m.asid}</a>
                  {m.eid ? <div>Listing #{m.eid}</div> : null}
                  {m.uid ? <div>Owner #{m.uid}</div> : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MatchStolenDialog;
